/**
 * Initial state factory. Pure: same seed + config -> identical GameState.
 */

import { createRng } from './rng.ts';
import { DEFAULT_CONFIG, type GameConfig, type GameState, type FlagState } from './types.ts';
import { type MazeConfig, generateMaze } from './maze.ts';
import { spawnCar } from './movement.ts';
import { createEnemies } from './enemies.ts';
import { enemiesForStage, fuelForStage, isChallengingStage } from './stage.ts';

export type InitOptions = {
  readonly seed: number;
  readonly config?: GameConfig;
  readonly mazeConfig?: MazeConfig;
  /** Override enemy count for tests; otherwise derived from stage. */
  readonly enemyCount?: number;
  /** Start at a specific stage (default 1). Used by tests for Phase 6+. */
  readonly stage?: number;
};

export function createGame(opts: InitOptions): GameState {
  const config = opts.config ?? DEFAULT_CONFIG;
  const stage = opts.stage ?? 1;
  // Per-stage seed: baseSeed + stage. Stage 1 uses opts.seed + 1.
  const stageSeed = opts.seed + stage;
  const maze = opts.mazeConfig ? generateMaze(stageSeed, opts.mazeConfig) : generateMaze(stageSeed);
  const flags: FlagState[] = maze.flags.map((f) => ({
    tile: f.tile,
    kind: f.kind,
    collected: false,
  }));
  const challenging = isChallengingStage(stage, config);
  // AIDEV-NOTE: Challenging Stages naturally spawn zero enemies (matches
  // Original Rally-X). Tests may force a count via opts.enemyCount.
  const enemyCount = opts.enemyCount ?? (challenging ? 0 : enemiesForStage(stage, config));
  return {
    tick: 0,
    phase: 'playing',
    rng: createRng(opts.seed),
    config,
    baseSeed: opts.seed,
    stage,
    challenging,
    maze,
    player: spawnCar(maze.spawn.x, maze.spawn.y),
    flags,
    enemies: createEnemies(maze, enemyCount, stage),
    smokes: [],
    score: 0,
    chainStep: 0,
    scoreMultiplier: 1,
    speedBoostUntil: 0,
    yellowCollected: 0,
    fuel: fuelForStage(stage, config),
    lives: config.startingLives,
    nextExtraLifeAt: config.extraLifeFirst,
    respawnAt: 0,
    deathCause: null,
    nextStageAt: 0,
    stageStartTick: 0,
    events: [],
  };
}
